/**
 * UsageMeter — shows the user's credit balance and (for free plan) how many AI
 * build sessions they have left today. Used in the sidebar account menu and the
 * AI Builder workspace header so usage is always visible.
 *
 * Data: GET /api/ai/meter → { plan, balance_cents, balance_usd, free? }
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { WalletIcon as Wallet, Wrench01Icon as Wrench } from '@hugeicons/core-free-icons';
import { useEffect, useState } from 'react';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useNavigate } from 'react-router-dom';

interface Meter {
  plan: string;
  balance_usd: string;
  balance_cents: number;
  free: { sessions_today: number; sessions_limit: number; steps_per_session: number; sessions_left: number } | null;
}

export function UsageMeter({ variant = 'menu' }: { variant?: 'menu' | 'compact' }) {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const [meter, setMeter] = useState<Meter | null>(null);

  useEffect(() => {
    let alive = true;
    authFetch('/api/ai/meter')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d && !d.error) setMeter(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [authFetch]);

  if (!meter) return null;

  const isFree = meter.plan === 'free';
  const left = meter.free?.sessions_left ?? 0;
  const limit = meter.free?.sessions_limit ?? 0;

  // Compact pill for the builder header.
  if (variant === 'compact') {
    return (
      <button
        onClick={() => navigate('/settings?tab=billing')}
        className="flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-card/40 border border-border/40 text-[11px] hover:bg-card/60 transition-colors"
        title={isFree ? `${left} of ${limit} free builds left today` : 'Credit balance'}
      >
        {isFree ? (
          <><HugeiconsIcon icon={Wrench} className="h-3 w-3 text-accent"  /><span className="font-medium">{left}/{limit} builds</span></>
        ) : (
          <><HugeiconsIcon icon={Wallet} className="h-3 w-3 text-accent"  /><span className="font-medium">${meter.balance_usd}</span></>
        )}
      </button>
    );
  }

  // Full block for the sidebar account dropdown.
  return (
    <div className="px-2.5 py-2 rounded-xl bg-sidebar-accent/40 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground flex items-center gap-1.5"><HugeiconsIcon icon={Wallet} className="h-3.5 w-3.5"  /> Credits</span>
        <span className="text-sm font-semibold">${meter.balance_usd}</span>
      </div>
      {isFree && meter.free && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>Free builds today</span>
            <span>{meter.free.sessions_today}/{limit}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted/40 overflow-hidden">
            <div
              className="h-full rounded-full bg-accent transition-all"
              style={{ width: `${limit ? Math.min(100, (meter.free.sessions_today / limit) * 100) : 0}%` }}
            />
          </div>
          <p className="text-[9px] text-muted-foreground/70">{left} left · up to {meter.free.steps_per_session} steps each</p>
        </div>
      )}
      <button
        onClick={() => navigate('/settings?tab=billing')}
        className="w-full text-[11px] font-medium text-accent hover:underline text-left"
      >
        {isFree ? 'Add credits →' : 'Manage billing →'}
      </button>
    </div>
  );
}
